import { useContext, useEffect, useState } from "react";
import { refreshContext } from "../contexts/refreshContext";
import { Accessibility } from "../models/Accessibility";
import { PropertyAccessibilityImages } from "../models/PropertyAccessibilityImages";
import { AccessibilityUnitLevelQuery } from "../models/AccessibilityLevel";
import {
  AccessibilityUnit,
} from "../models/AccessibilityUnits";
import { Property } from "../models/Property";
import {
  retrieveAccessibilityUnitsWithFields,
  retrieveAccessibilityByPropertyId,
  listAccessibilityImagesByPropertyId,
  retrievePropertySubCategoryByKey,
  retrieveAccessibilityUnitsLevelsByPropertyIdAndType,
} from "../services/property.service";
import {replaceAccessibilitiesTitle} from "../utils/accessibilities";
import {A11yStandardType} from "../models/A11yStandard";

export const useFetchDetailedPropertyData = (shop: Property | undefined, a11yStandard: A11yStandardType) => {

  const [loading, setLoading] = useState(false);
  const [refresh] = useContext(refreshContext);

  const [propertyItemAccessibilityUnits, setPropertyItemAccessibilityUnits] = useState<AccessibilityUnit[]>([]);
  const [itemAccessibilities, setItemAccessibilities] = useState<Accessibility[]>([]);
  const [propertyItemImages, setPropertyItemImages] = useState<PropertyAccessibilityImages[]>([]);
  const [propertyItemCategory, setPropertyItemCategory] = useState("");
  const [propertyItemFloors, setPropertyItemFloors] = useState<string>("");

  const handlerFloors = (unitLevel: AccessibilityUnitLevelQuery) => {
    if (unitLevel.results && unitLevel.results.length > 1) {
      let minFloor = unitLevel.results[0].level;
      let maxFloor = unitLevel.results[0].level;
      unitLevel.results.forEach((floorObj) => {
        if (floorObj.level < minFloor) {
          minFloor = floorObj.level;
        }
        if (floorObj.level > maxFloor) {
          maxFloor = floorObj.level;
        }
      })
      if (minFloor === maxFloor) {
        setPropertyItemFloors(unitLevel.results[0].level.toString());
      } else {
        setPropertyItemFloors(`${minFloor} - ${maxFloor}`);
      }
    } else if (unitLevel.results && unitLevel.results.length === 1) {
      setPropertyItemFloors(unitLevel.results[0].level.toString());
    } else {
      setPropertyItemFloors("")
    }
  }

  useEffect(() => {    
    const fetchData = async () => {
      setLoading(true);
      const accessibilityUnits = shop ? (
        await retrieveAccessibilityUnitsWithFields(shop.id, "id,name,accessibility_unit,title,is_accessible_unit,order")
      ) : null;
      setPropertyItemAccessibilityUnits(accessibilityUnits && accessibilityUnits.results ?
        accessibilityUnits.results.sort((a,b) => a.order - b.order) :
        []
      )
      setLoading(false);
    }
    fetchData().catch(console.error);
    return () => {
      setPropertyItemAccessibilityUnits([]);
      setLoading(false);
    }
  }, [refresh, shop])

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const accessibilities = shop ? (
        await retrieveAccessibilityByPropertyId(shop.id, "id,item,attribute,dynamic_value,item_title,attribute_title,attribute_icon,accessibility_unit,is_absent,disability_types")
      ) : null;
      if (accessibilities && accessibilities.results.length === 0) {
      } else if (accessibilities) {
        const filteredAccessibilities = accessibilities.results.filter(
          (access) => !access.is_absent
        );
        if (filteredAccessibilities) {
          const filterOutNull = filteredAccessibilities.filter((i) => i.attribute_title !== null)
          setItemAccessibilities(replaceAccessibilitiesTitle(filterOutNull, propertyItemAccessibilityUnits, a11yStandard));
        }
      }
      setLoading(false);
    }
    fetchData().catch(console.error);
    return () => {
      setItemAccessibilities([]);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, shop])

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const images = shop ? (
        await listAccessibilityImagesByPropertyId(shop.id)
      ) : null;
      if (images) {
        setPropertyItemImages(images.results);        
      }
      setLoading(false);
    }
    fetchData().catch(console.error);
    return () => {
      setPropertyItemImages([]);  
      setLoading(false);
    }
  }, [refresh, shop])

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const subCat = shop ? (
        await retrievePropertySubCategoryByKey(shop.sub_category)
      ) : null;
      if (subCat) {
        if (subCat.results.length > 0 && subCat.results[0].title) {
          setPropertyItemCategory(subCat.results[0].title);
        } else {
          setPropertyItemCategory("");
        }
      }
      setLoading(false);
    }
    fetchData().catch(console.error);
    return () => {
      setPropertyItemCategory("");
      setLoading(false);
    }
  }, [refresh, shop])

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const floors = shop ? (
        await retrieveAccessibilityUnitsLevelsByPropertyIdAndType(shop.id, "entrance")
      ) : null;
      if (floors) {
        handlerFloors(floors)
      }
      setLoading(false);
    }
    fetchData().catch(console.error);
    return () => {
      setPropertyItemFloors("")
      setLoading(false);
    }
  }, [refresh, shop])

  return {
    loading,
    propertyItemAccessibilityUnits,
    itemAccessibilities,
    propertyItemImages, 
    propertyItemCategory,
    propertyItemFloors,
  };
};